import { formatNumber } from '@/shared/utils/format';
import { useDrugDetail } from '@/entities/drug/hooks/useDrugQueries';
import { shortDrugName } from '@/entities/drug/utils/drug.utils';
import { DrugDetailView } from '@/shared/components/drug-drawer/DrugDetailView';
import { useDrugDrawer } from '@/shared/components/drug-drawer/useDrugDrawer';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from '@/shared/components/ui/sheet';
import { cn } from '@/shared/lib/utils';
import { toastApi } from '@/shared/utils/toast';
import { useUIStore } from '@/stores/uiStore';
import { useSearch } from '@tanstack/react-router';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { Fragment, useEffect, useRef, useState } from 'react';

/** 브레드크럼 한 칸 — 드로어 안에서 거쳐 온 약 */
interface Crumb {
  drugCode: string;
  drugName: string;
}

/**
 * M-12 의약품 상세 드로어 — 앱 레이아웃에 한 번만 둔다.
 * 열림 상태는 URL ?drug=보험코드 하나로 관리한다 (M-15 D).
 * 여는 쪽은 useDrugDrawer().open(drugCode)를 쓴다.
 *
 * 드로어 안에서 동일성분 약을 누르면 새로 쌓지 않고 내용만 바꾼다(M-15 B 교체).
 * 거쳐 온 약은 브레드크럼 스택으로 들고 있고, [이전]·브레드크럼으로 돌아간다.
 */
export const DrugDrawerHost = () => {
  const { drug } = useSearch({ strict: false }) as { drug?: string };
  const { close, replace } = useDrugDrawer();
  // 거래 정보 드로어 위에 2단으로 쌓일 때 24px 왼쪽으로 비켜 연다 (M-15 B). 모바일은 전체 폭 교체
  const stacked = useUIStore((s) => s.drugDrawerStacked);
  const drugContext = useUIStore((s) => s.drugContext);
  const setDrugContext = useUIStore((s) => s.setDrugContext);
  // 드로어 안에서 다른 약으로 바뀌면 처방량 문맥은 보여 주지 않는다
  const context = drugContext?.drugCode === drug ? drugContext : null;
  const [trail, setTrail] = useState<Crumb[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);

  // 닫히면 브레드크럼을 비운다. 스택에 없는 약으로 바뀐 경우(외부 이동)도 새로 시작한다
  useEffect(() => {
    if (!drug) {
      setTrail([]);
      setDrugContext(null);
    }
  }, [drug, setDrugContext]);

  // 약이 바뀌면 본문을 맨 위로 올린다
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
  }, [drug]);

  const selectRelated = (current: Crumb, nextCode: string) => {
    setTrail((t) => [...t, current]);
    void replace(nextCode);
  };

  const goToCrumb = (index: number) => {
    const target = trail[index];
    if (!target) return;
    setTrail((t) => t.slice(0, index));
    void replace(target.drugCode);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toastApi.info('링크가 복사되었습니다');
    } catch {
      toastApi.error('링크를 복사하지 못했어요. 주소창의 주소를 복사해주세요.');
    }
  };

  return (
    <Sheet open={!!drug} onOpenChange={(open) => !open && close()}>
      <SheetContent
        side="right"
        overlay="drawer"
        className={cn(stacked && 'lg:right-6')}
      >
        <header className="flex shrink-0 items-center gap-3 border-b border-line py-[18px] pr-4 pl-6">
          <button
            type="button"
            onClick={close}
            aria-label="닫기"
            className="-m-1 rounded-sm p-1 text-ink-3 outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          >
            <X className="size-5" />
          </button>
          <SheetTitle className="text-base leading-6 font-bold text-ink">
            의약품 상세
          </SheetTitle>
          <button
            type="button"
            onClick={() => void copyLink()}
            className="ml-auto rounded-[9px] bg-muted px-3.5 py-[9px] text-xs leading-[18px] font-medium text-ink-2 hover:bg-[#e8e8e8]"
          >
            링크 복사
          </button>
        </header>
        <SheetDescription className="sr-only">
          보험코드 {drug} 의약품 상세
        </SheetDescription>

        {/* M-15 C — 실적에서 열었을 때 그 달 그 병의원의 처방량 */}
        {context && (
          <p className="shrink-0 border-b border-line-soft bg-primary-tint px-6 py-2.5 text-xs leading-[18px] text-ink-3">
            {context.label} 처방량{' '}
            <span className="font-bold text-ink">
              {formatNumber(context.quantity)}
            </span>
          </p>
        )}

        {trail.length > 0 && (
          <nav
            aria-label="거쳐 온 의약품"
            className="flex shrink-0 items-center gap-2 border-b border-line-soft bg-subtle px-6 py-2.5 text-xs leading-[18px]"
          >
            <button
              type="button"
              onClick={() => goToCrumb(trail.length - 1)}
              className="flex items-center gap-0.5 font-medium text-ink-2 hover:text-ink"
            >
              <ChevronLeft className="size-4" />
              이전
            </button>
            <span className="h-3 w-px bg-line-strong" />
            <ol className="flex min-w-0 items-center gap-1 overflow-x-auto whitespace-nowrap text-ink-6">
              {trail.map((c, i) => (
                <Fragment key={`${c.drugCode}-${i}`}>
                  <li>
                    <button
                      type="button"
                      onClick={() => goToCrumb(i)}
                      className="hover:text-ink hover:underline"
                    >
                      {shortDrugName(c.drugName)}
                    </button>
                  </li>
                  <ChevronRight className="size-3 shrink-0 text-ink-8" />
                </Fragment>
              ))}
              <li aria-current="page" className="font-medium text-ink">
                <CurrentCrumb drugCode={drug} />
              </li>
            </ol>
          </nav>
        )}

        <div ref={scrollRef} className="flex-1 overflow-y-auto">
          {drug && (
            <DrugDetailView
              key={drug}
              drugCode={drug}
              onSelectRelated={selectRelated}
            />
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

/** 현재 약 이름 — 상세 캐시에서 읽는다 (DrugDetailView와 같은 쿼리) */
const CurrentCrumb = ({ drugCode }: { drugCode?: string }) => {
  const { data } = useDrugDetail(drugCode);
  return <>{data ? shortDrugName(data.drug.drugName) : drugCode}</>;
};
